import {
  Component,
  EventEmitter,
  OnInit,
  ViewChild,
  Output,
  Input,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { ChallengeQuestionService } from '@login/services/challenge-question.service';
import { ResetPasswordService } from '@login/services/reset-password.service';
import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { SpinnerService } from '@shared/services/spinner.service';

const CHALLENGE_ANSWER_PATTERN = '^(?! )[a-zA-Z0-9!@#$%^&*|?:;\\s]+[^ +<>()]$';
const PATTERN_ERR_MSG = 'Answer is invalid';
const MIN_LENGTH_ERR_MSG = 'Answer should be at least 4 characters';
const MAX_LENGTH_ERR_MSG = 'Answer should be within 100 characters';

@Component({
  selector: 'app-challenge-question-form',
  templateUrl: './challenge-question-form.component.html',
  styleUrls: ['./challenge-question-form.component.scss'],
})
export class ChallengeQuestionFormComponent implements OnInit {
  @Output('isValidRecoveryToken')
  isValidRecoveryTokenEvent = new EventEmitter();
  @Output() verifyAnswerSuccess = new EventEmitter();
  @Output() verifyAnswerError = new EventEmitter();
  @Input() service;

  stateToken: string;

  challengeQuestionForm: UntypedFormGroup;
  challengeQuestionAnswerConfig: InputConfig;

  constructor(
    private resetPasswordService: ResetPasswordService,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private challengeQuestionService: ChallengeQuestionService,
    private fb: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.getChallengeQuestion();
    this.initForm();
  }

  initForm() {
    this.challengeQuestionForm = this.fb.group({
      answer: [
        '',
        [
          HarpValidators.required(),
          HarpValidators.pattern(CHALLENGE_ANSWER_PATTERN, PATTERN_ERR_MSG),
          HarpValidators.minLength(4, MIN_LENGTH_ERR_MSG),
          HarpValidators.maxLength(100, MAX_LENGTH_ERR_MSG),
        ],
      ],
    });

    this.challengeQuestionAnswerConfig = {
      form: this.challengeQuestionForm,
      type: 'text',
      required: true,
      label: '',
      name: 'answer',
      autofocus: true,
    };
  }

  getChallengeQuestion() {
    this.spinnerService.start('Loading...');
    const recoveryToken = this.route.snapshot.params['recoveryToken'];
    this.challengeQuestionService
      .requestSecretQuestion({ service: this.service, recoveryToken })
      .subscribe(
        (response: any) => this.getChallengeQuestionSuccess(response),
        (err: any) => this.getChallengeQuestionError(err),
      );
  }

  private getChallengeQuestionSuccess(response) {
    this.spinnerService.stop();

    if (response.errorCode) {
      this.isValidRecoveryTokenEvent.emit({ isValid: false, err: response });
      return;
    }
    const { stateToken, recoveryQuestion } = response;
    this.isValidRecoveryTokenEvent.emit({ isValid: true });
    this.challengeQuestionAnswerConfig.label = recoveryQuestion;
    this.stateToken = stateToken;
    this.challengeQuestionService.stateToken = stateToken;
  }

  private getChallengeQuestionError(err) {
    this.isValidRecoveryTokenEvent.emit({
      isValid: false,
      err,
    });
    this.spinnerService.stop();
  }

  onSubmit() {
    this.spinnerService.start();
    this.challengeQuestionService
      .requestSecretAnswerVerification({
        service: this.service,
        answer: this.answer,
        stateToken: this.stateToken,
      })
      .subscribe(
        res => {
          this.spinnerService.stop();
          this.verifyAnswerSuccess.emit(res);
        },
        err => {
          this.spinnerService.stop();
          this.verifyAnswerError.emit(err);
        },
      );
  }

  get answer() {
    return this.challengeQuestionForm.value?.answer;
  }
}
